<template>
  <div class="ai">
    <div class="ai-topbar">
      <div>
        <h5>Agente de IA</h5>
        <span class="ai-subtitle">Configure o assistente e sua base de conhecimento</span>
      </div>
    </div>

    <div v-if="statusMessage" class="ai-toast" :class="'ai-toast-' + statusVariant" @click="statusMessage = ''">
      <feather-icon :icon="statusVariant === 'success' ? 'CheckCircleIcon' : 'XCircleIcon'" size="16" />
      <span>{{ statusMessage }}</span>
      <feather-icon icon="XIcon" size="14" class="ai-toast-x" />
    </div>

    <div class="ai-card">
      <div class="ai-card-head">
        <div class="ai-card-icon purple"><feather-icon icon="CpuIcon" size="16" /></div>
        <span>Configurações do Agente</span>
      </div>

      <div class="ai-card-body">
        <div class="ai-grid">
          <div class="ai-field">
            <label><feather-icon icon="UserIcon" size="13" /> Nome do Agente</label>
            <input v-model="settings.nome" placeholder="Ex: Assistente AV Market" class="ai-input" />
          </div>
          <div class="ai-field">
            <label><feather-icon icon="MessageCircleIcon" size="13" /> Tom de Voz</label>
            <select v-model="settings.tom" class="ai-input ai-select">
              <option value="profissional">Profissional</option>
              <option value="amigavel">Amigável</option>
              <option value="formal">Formal</option>
              <option value="casual">Casual</option>
              <option value="tecnico">Técnico</option>
            </select>
          </div>
          <div class="ai-field">
            <label><feather-icon icon="GlobeIcon" size="13" /> Idioma</label>
            <select v-model="settings.idioma" class="ai-input ai-select">
              <option value="pt-BR">Português (BR)</option>
              <option value="en-US">English (US)</option>
              <option value="es-ES">Español</option>
            </select>
          </div>
        </div>

        <div class="ai-api-box">
          <div class="ai-api-label"><feather-icon icon="KeyIcon" size="14" /> Chave da API OpenAI</div>
          <div class="ai-api-row">
            <div class="ai-api-input-wrap">
              <input
                v-model="settings.openai_api_key"
                :type="showApiKey ? 'text' : 'password'"
                placeholder="sk-..."
                autocomplete="off"
                class="ai-input"
              />
              <button class="ai-api-eye" @click="showApiKey = !showApiKey">
                <feather-icon :icon="showApiKey ? 'EyeOffIcon' : 'EyeIcon'" size="14" />
              </button>
            </div>
            <div class="ai-api-badge" :class="'badge-' + apiKeyStatus.variant">
              <feather-icon :icon="apiKeyStatus.icon" size="13" />
              <span>{{ apiKeyStatus.text }}</span>
            </div>
          </div>
          <small class="ai-api-hint">
            Necessária para geração de imagens e conteúdo.
            <a href="https://platform.openai.com/api-keys" target="_blank">Obter chave</a>
          </small>
        </div>

        <button class="ai-btn ai-btn-primary" @click="saveSettings" :disabled="saving">
          <b-spinner v-if="saving" small />
          <feather-icon v-else icon="SaveIcon" size="14" />
          Salvar Configurações
        </button>
      </div>
    </div>

    <div class="ai-card mt-3">
      <div class="ai-card-head">
        <div class="ai-card-icon green"><feather-icon icon="DatabaseIcon" size="16" /></div>
        <span>Base de Conhecimento</span>
        <button class="ai-btn ai-btn-primary ai-btn-sm ml-auto" @click="showAddForm = !showAddForm">
          <feather-icon icon="PlusIcon" size="13" />
          Adicionar
        </button>
      </div>

      <div class="ai-card-body">
        <div v-if="showAddForm" class="ai-add-box">
          <div class="ai-add-grid">
            <div class="ai-field">
              <label>Título</label>
              <input v-model="newItem.titulo" placeholder="Título do conhecimento" class="ai-input" />
            </div>
            <div class="ai-field">
              <label>Categoria</label>
              <select v-model="newItem.categoria" class="ai-input ai-select">
                <option value="general">Geral</option>
                <option value="services">Serviços</option>
                <option value="company">Empresa</option>
                <option value="products">Produtos</option>
                <option value="blog_style">Estilo do Blog</option>
              </select>
            </div>
            <div class="ai-field full">
              <label>Conteúdo</label>
              <textarea v-model="newItem.conteudo" placeholder="Informação para a IA usar como contexto..." rows="2" class="ai-input ai-textarea"></textarea>
            </div>
          </div>
          <div class="ai-add-actions">
            <button class="ai-btn ai-btn-primary ai-btn-sm" @click="addTrainingData" :disabled="!newItem.titulo || !newItem.conteudo">
              <feather-icon icon="PlusIcon" size="13" /> Adicionar
            </button>
            <button class="ai-btn ai-btn-ghost ai-btn-sm" @click="showAddForm = false">Cancelar</button>
          </div>
        </div>

        <div v-if="loadingTD" class="ai-loading"><b-spinner variant="primary" /></div>
        <div v-else-if="!trainingData.length && !showAddForm" class="ai-empty">
          <feather-icon icon="DatabaseIcon" size="32" />
          <p>Nenhum dado de treinamento adicionado</p>
        </div>
        <table v-else-if="trainingData.length" class="ai-table">
          <thead>
            <tr>
              <th>Título</th>
              <th>Categoria</th>
              <th>Conteúdo</th>
              <th>Ativo</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in trainingData" :key="item.id">
              <td><strong>{{ item.titulo }}</strong></td>
              <td>
                <span class="ai-cat" :class="'cat-' + categoryVariant(item.categoria)">{{ categoryLabel(item.categoria) }}</span>
              </td>
              <td><span class="ai-preview">{{ item.conteudo }}</span></td>
              <td>
                <b-form-checkbox :checked="!!item.ativo" switch @change="toggleActive(item)" />
              </td>
              <td>
                <div class="ai-acts">
                  <button class="ai-act edit" @click="editItem(item)"><feather-icon icon="EditIcon" size="14" /></button>
                  <button class="ai-act del" @click="deleteItem(item)"><feather-icon icon="TrashIcon" size="14" /></button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <b-modal v-model="showEditModal" title="Editar Conhecimento" ok-title="Salvar" cancel-title="Cancelar" @ok="saveEditItem">
      <div class="ai-field mb-2">
        <label>Título</label>
        <input v-model="editingItem.titulo" class="ai-input" />
      </div>
      <div class="ai-field mb-2">
        <label>Categoria</label>
        <select v-model="editingItem.categoria" class="ai-input ai-select">
          <option value="general">Geral</option>
          <option value="services">Serviços</option>
          <option value="company">Empresa</option>
          <option value="products">Produtos</option>
          <option value="blog_style">Estilo do Blog</option>
        </select>
      </div>
      <div class="ai-field">
        <label>Conteúdo</label>
        <textarea v-model="editingItem.conteudo" rows="4" class="ai-input ai-textarea"></textarea>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BRow, BCol, BButton,
  BFormGroup, BFormInput, BFormSelect, BFormTextarea, BFormCheckbox,
  BTable, BAlert, BSpinner, BBadge, BModal,
  BInputGroup, BInputGroupAppend,
} from 'bootstrap-vue'
import axios from '@axios'

export default {
  components: {
    BCard, BCardHeader, BCardBody, BRow, BCol, BButton,
    BFormGroup, BFormInput, BFormSelect, BFormTextarea, BFormCheckbox,
    BTable, BAlert, BSpinner, BBadge, BModal,
    BInputGroup, BInputGroupAppend,
  },
  data() {
    return {
      settings: { nome: '', tom: 'profissional', idioma: 'pt-BR', openai_api_key: '' },
      showApiKey: false,
      saving: false,
      trainingData: [],
      loadingTD: false,
      showAddForm: false,
      newItem: { titulo: '', conteudo: '', categoria: 'general' },
      showEditModal: false,
      editingItem: { id: null, titulo: '', conteudo: '', categoria: 'general' },
      statusMessage: '',
      statusVariant: 'success',
      tdFields: [
        { key: 'titulo', label: 'Título', sortable: true },
        { key: 'categoria', label: 'Categoria', sortable: true },
        { key: 'conteudo', label: 'Conteúdo', sortable: false },
        { key: 'ativo', label: 'Ativo', sortable: false },
        { key: 'acoes', label: 'Ações', sortable: false },
      ],
    }
  },
  computed: {
    apiKeyStatus() {
      const key = this.settings.openai_api_key || ''
      if (!key) return { variant: 'warning', icon: 'AlertCircleIcon', text: 'Nenhuma chave configurada' }
      if (key.includes('...')) return { variant: 'success', icon: 'CheckCircleIcon', text: 'Chave configurada' }
      if (key.startsWith('sk-') && key.length > 20) return { variant: 'info', icon: 'InfoIcon', text: 'Nova chave — salve para aplicar' }
      return { variant: 'danger', icon: 'XCircleIcon', text: 'Formato inválido' }
    },
  },
  created() {
    this.loadSettings()
    this.loadTrainingData()
  },
  methods: {
    async loadSettings() {
      try {
        const { data } = await axios.get('blog/ai-agent/settings')
        this.settings = { nome: data.nome, tom: data.tom, idioma: data.idioma, openai_api_key: data.openai_api_key || '' }
      } catch (e) { console.error(e) }
    },
    async saveSettings() {
      this.saving = true
      try {
        await axios.put('blog/ai-agent/settings', this.settings)
        await this.loadSettings()
        this.statusMessage = 'Configurações salvas!'
        this.statusVariant = 'success'
      } catch (e) {
        this.statusMessage = 'Erro ao salvar'
        this.statusVariant = 'danger'
      }
      this.saving = false
    },
    async loadTrainingData() {
      this.loadingTD = true
      try {
        const { data } = await axios.get('blog/ai-agent/training')
        this.trainingData = data
      } catch (e) { console.error(e) }
      this.loadingTD = false
    },
    async addTrainingData() {
      try {
        await axios.post('blog/ai-agent/training', this.newItem)
        this.newItem = { titulo: '', conteudo: '', categoria: 'general' }
        this.showAddForm = false
        this.loadTrainingData()
        this.statusMessage = 'Conhecimento adicionado!'
        this.statusVariant = 'success'
      } catch (e) {
        this.statusMessage = 'Erro ao adicionar'
        this.statusVariant = 'danger'
      }
    },
    editItem(item) {
      this.editingItem = { id: item.id, titulo: item.titulo, conteudo: item.conteudo, categoria: item.categoria }
      this.showEditModal = true
    },
    async saveEditItem() {
      try {
        await axios.put(`blog/ai-agent/training/${this.editingItem.id}`, this.editingItem)
        this.loadTrainingData()
        this.statusMessage = 'Atualizado!'
        this.statusVariant = 'success'
      } catch (e) {
        this.statusMessage = 'Erro ao atualizar'
        this.statusVariant = 'danger'
      }
    },
    async deleteItem(item) {
      if (!confirm(`Excluir "${item.titulo}"?`)) return
      try {
        await axios.delete(`blog/ai-agent/training/${item.id}`)
        this.loadTrainingData()
      } catch (e) {
        this.statusMessage = 'Erro ao excluir'
        this.statusVariant = 'danger'
      }
    },
    async toggleActive(item) {
      try {
        await axios.patch(`blog/ai-agent/training/${item.id}/toggle`)
        this.loadTrainingData()
      } catch (e) { console.error(e) }
    },
    categoryVariant(cat) {
      const m = { general: 'gray', services: 'blue', company: 'purple', products: 'green', blog_style: 'amber' }
      return m[cat] || 'gray'
    },
    categoryLabel(cat) {
      const m = { general: 'Geral', services: 'Serviços', company: 'Empresa', products: 'Produtos', blog_style: 'Estilo' }
      return m[cat] || cat
    },
  },
}
</script>

<style scoped>
*{box-sizing:border-box}
.ai{font-family:Inter,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#1a1a2e}

.ai-topbar{margin-bottom:20px}
.ai-topbar h5{font-size:1.3rem;font-weight:800;margin:0 0 2px;color:#1a1a2e}
.ai-subtitle{font-size:.82rem;color:#94a3b8}

.ai-toast{display:flex;align-items:center;gap:10px;padding:12px 18px;border-radius:12px;margin-bottom:16px;font-size:.85rem;font-weight:500;cursor:pointer;animation:slideDown .3s ease}
.ai-toast-x{margin-left:auto;opacity:.6}
.ai-toast-success{background:#ecfdf5;color:#065f46;border:1px solid #a7f3d0}
.ai-toast-danger{background:#fef2f2;color:#991b1b;border:1px solid #fecaca}
@keyframes slideDown{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}

.ai-card{background:#fff;border:1px solid #e2e8f0;border-radius:14px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,.04)}
.ai-card-head{display:flex;align-items:center;gap:10px;padding:16px 20px;border-bottom:1px solid #f1f5f9;font-size:.92rem;font-weight:700;color:#1a1a2e}
.ai-card-icon{width:32px;height:32px;border-radius:10px;display:flex;align-items:center;justify-content:center;color:#fff;flex-shrink:0}
.ai-card-icon.purple{background:linear-gradient(135deg,#7c3aed,#a78bfa)}
.ai-card-icon.green{background:linear-gradient(135deg,#10b981,#6ee7b7)}
.ai-card-body{padding:20px}

.ai-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:16px;margin-bottom:20px}
.ai-field{display:flex;flex-direction:column;gap:6px}
.ai-field label{font-size:.75rem;font-weight:700;text-transform:uppercase;letter-spacing:.4px;color:#475569;display:flex;align-items:center;gap:5px}
.ai-field.full{grid-column:1/-1}
.ai-input{width:100%;padding:9px 12px;border:1.5px solid #e2e8f0;border-radius:10px;font-size:.85rem;color:#1a1a2e;background:#fff;outline:none;transition:all .2s;font-family:inherit}
.ai-input:focus{border-color:#7c3aed;box-shadow:0 0 0 3px rgba(124,58,237,.1)}
.ai-input::placeholder{color:#94a3b8}
.ai-textarea{resize:vertical;min-height:50px}
.ai-select{appearance:none;background-image:url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");background-position:right 8px center;background-repeat:no-repeat;background-size:18px;padding-right:32px;cursor:pointer}

.ai-api-box{background:#fafbfc;border:1px solid #e2e8f0;border-radius:12px;padding:16px;margin-bottom:20px}
.ai-api-label{font-size:.78rem;font-weight:700;color:#475569;display:flex;align-items:center;gap:6px;margin-bottom:10px;text-transform:uppercase;letter-spacing:.3px}
.ai-api-row{display:flex;gap:12px;flex-wrap:wrap;align-items:flex-start}
.ai-api-input-wrap{position:relative;flex:1;min-width:240px}
.ai-api-input-wrap .ai-input{padding-right:40px}
.ai-api-eye{position:absolute;right:10px;top:50%;transform:translateY(-50%);background:none;border:none;color:#94a3b8;cursor:pointer;padding:4px}
.ai-api-eye:hover{color:#7c3aed}
.ai-api-badge{display:flex;align-items:center;gap:6px;padding:8px 14px;border-radius:10px;font-size:.78rem;font-weight:600;flex-shrink:0;max-width:280px}
.ai-api-badge.badge-success{background:#ecfdf5;color:#10b981}
.ai-api-badge.badge-warning{background:#fffbeb;color:#f59e0b}
.ai-api-badge.badge-info{background:#eff6ff;color:#3b82f6}
.ai-api-badge.badge-danger{background:#fef2f2;color:#ef4444}
.ai-api-hint{display:block;margin-top:8px;font-size:.75rem;color:#94a3b8}
.ai-api-hint a{color:#7c3aed;font-weight:600}

.ai-btn{display:inline-flex;align-items:center;gap:6px;padding:9px 18px;border-radius:10px;font-size:.82rem;font-weight:600;border:none;cursor:pointer;transition:all .2s cubic-bezier(.4,0,.2,1)}
.ai-btn:disabled{opacity:.5;cursor:not-allowed}
.ai-btn-primary{background:#7c3aed;color:#fff}
.ai-btn-primary:hover:not(:disabled){background:#6d28d9;box-shadow:0 4px 14px rgba(124,58,237,.3);transform:translateY(-1px)}
.ai-btn-ghost{background:#f8fafc;color:#64748b;border:1px solid #e2e8f0}
.ai-btn-ghost:hover{background:#f1f5f9}
.ai-btn-sm{padding:7px 14px;font-size:.78rem}
.ml-auto{margin-left:auto}

.ai-add-box{border:1.5px solid #e2e8f0;border-radius:12px;padding:16px;margin-bottom:16px;background:#fafbfc}
.ai-add-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:12px}
.ai-add-actions{display:flex;gap:8px}

.ai-loading{display:flex;justify-content:center;padding:40px}
.ai-empty{display:flex;flex-direction:column;align-items:center;padding:40px;color:#cbd5e1}
.ai-empty p{font-size:.85rem;margin:8px 0 0;color:#94a3b8}

.ai-table{width:100%;border-collapse:collapse}
.ai-table thead th{padding:10px 14px;font-size:.72rem;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:#94a3b8;border-bottom:1px solid #f1f5f9;text-align:left}
.ai-table tbody td{padding:12px 14px;border-bottom:1px solid #f8fafc;vertical-align:middle}
.ai-table tbody tr:hover{background:#faf5ff}
.ai-table strong{font-size:.85rem;color:#1a1a2e}

.ai-cat{font-size:.7rem;font-weight:700;padding:3px 10px;border-radius:6px;display:inline-block}
.ai-cat.cat-gray{background:#f1f5f9;color:#64748b}
.ai-cat.cat-blue{background:#eff6ff;color:#3b82f6}
.ai-cat.cat-purple{background:#f5f3ff;color:#7c3aed}
.ai-cat.cat-green{background:#ecfdf5;color:#10b981}
.ai-cat.cat-amber{background:#fffbeb;color:#f59e0b}

.ai-preview{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;max-width:280px;font-size:.82rem;color:#64748b}

.ai-acts{display:flex;gap:2px}
.ai-act{width:30px;height:30px;border-radius:8px;border:none;background:transparent;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s}
.ai-act.edit{color:#7c3aed}
.ai-act.edit:hover{background:#f5f3ff}
.ai-act.del{color:#ef4444}
.ai-act.del:hover{background:#fef2f2}
</style>
